import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import cn from 'classnames'
import { useQuery } from '@tanstack/react-query'
import { Check, ChevronRight, Coins, Copy, Info, Link, Share2, TrendingUp } from 'lucide-react'
import TreechatIcon from 'components/shared/icons/TreechatIcon'
import { EmailIcon, FacebookIcon, RedditIcon, TelegramIcon, TwitterIcon } from 'next-share'
import api from 'api/api'
import type { DailyEarningsBackingPost, DailyEarningsShare } from 'api/api'
import queryClient from 'api/queryClient'
import { openCenter } from 'components/filters/useStreamFilters'
import Tray from 'components/shared/Tray'
import DailyMoneyValue from 'components/shared/DailyMoneyValue'
import { parseDailyAmountDisplay } from 'components/shared/dailyMoney'
import ShareRewardsInfoTray from 'components/modals/ShareRewardsInfoTray'
import { copyShareCardToClipboard } from 'components/answers/AnswerControls'
import styles from 'components/modals/wallet.module.scss'
import trayStyles from 'components/shared/share-tray.module.scss'
import { insertToClipboard } from 'utils'
import { hasNativeCapability, shareTextOrUrlNative } from 'utils/nativeShare'
import {
    buildDailyEarningsShareText,
    buildShareDestination,
    getEarningsShareCardUrl,
    getEarningsShareUrl,
    resolveAnswerShareSurface,
} from 'utils/shareUtils'
import { dailyEarningsEnabled } from 'lib/dailyEarningsFeature'
import { isMobile } from 'lib/device'
import { questPath } from 'lib/seoPaths'

const EARNINGS_SHARE_TITLE = "Today's earnings on Treechat"

// Wallet "Today" section: what the user has earned so far this UTC day, with
// the share entry point. Sharing mints a DailyEarningsShare and opens a tray
// mirroring the post share tray (card hero + link + destinations).
export default function WalletDailyEarnings({ closeModal }: { closeModal?: () => void }) {
    const currentUserId = gon.currentUser?.id
    const earningsEnabled = dailyEarningsEnabled()
    const [shareTrayOpen, setShareTrayOpen] = useState(false)

    const { data } = useQuery({
        queryKey: ['daily-earnings-today', currentUserId],
        queryFn: api.getDailyEarningsToday,
        enabled: earningsEnabled && currentUserId != null,
        staleTime: 30_000,
    })

    if (!earningsEnabled || !currentUserId || !data) return null

    const satsEarned = data.sats_earned || 0
    const upvalueCount = data.upvalue_count || 0
    const amount = parseDailyAmountDisplay(data.amount_display)

    return (
        <div className={styles.earningsSection} data-testid="wallet-daily-earnings">
            <div className={styles.earningsHeader}>
                <div className={styles.earningsTitle}>
                    <TrendingUp size={15} aria-hidden />
                    <span>Today's earnings</span>
                </div>
                <div className={styles.earningsRight}>
                    <span
                        className={cn(
                            styles.earningsAmount,
                            satsEarned > 0 && styles.earningsAmountPositive,
                        )}
                    >
                        {amount ? <DailyMoneyValue money={amount} /> : data.amount_display}
                    </span>
                    {upvalueCount > 0 && (
                        <span className={styles.earningsCount}>
                            · {upvalueCount} {upvalueCount === 1 ? 'upvalue' : 'upvalues'}
                        </span>
                    )}
                    <button
                        type="button"
                        className={styles.earningsShareButton}
                        disabled={satsEarned <= 0}
                        title={
                            satsEarned <= 0
                                ? 'Earn upvalues today to share your day'
                                : "Share today's earnings"
                        }
                        onClick={() => setShareTrayOpen(true)}
                        data-testid="wallet-daily-earnings-share"
                    >
                        <Share2 size={13} aria-hidden />
                        share
                    </button>
                </div>
            </div>

            <EarningsShareTray
                isOpen={shareTrayOpen}
                onDismiss={() => setShareTrayOpen(false)}
                onOpenPost={closeModal}
            />
        </div>
    )
}

// Share tray for the daily-earnings card. Opening it mints (or refetches)
// the share record — today's by default, or the explicit ISO `day` a daily
// notification announced — then follows the AnswerShareTray surface rules:
// on phones the card hero opens the OS sheet, on desktop it copies the
// image, with copy-link and per-destination rows below.
// `onOpenPost` lets a host that sits over the panels (the wallet modal) get
// out of the way when "View" opens the posted thread in the center panel.
export function EarningsShareTray({
    isOpen,
    onDismiss,
    onOpenPost,
    day,
}: {
    isOpen: boolean
    onDismiss: () => void
    onOpenPost?: () => void
    day?: string
}) {
    const [share, setShare] = useState<DailyEarningsShare | null>(null)
    const [mintError, setMintError] = useState<string | null>(null)
    const [shareLinkCopied, setShareLinkCopied] = useState(false)
    const [shareCardSharing, setShareCardSharing] = useState(false)
    const [shareCardCopied, setShareCardCopied] = useState(false)
    const [shareCardError, setShareCardError] = useState<string | null>(null)
    const [shareCardActionError, setShareCardActionError] = useState<string | null>(null)
    const [shareCardBlob, setShareCardBlob] = useState<Blob | null>(null)
    const [shareCardPreviewUrl, setShareCardPreviewUrl] = useState<string | null>(null)
    const [shareCardPreviewLoading, setShareCardPreviewLoading] = useState(false)
    const [streamPosting, setStreamPosting] = useState(false)
    const [streamPosted, setStreamPosted] = useState(false)
    const [streamPostError, setStreamPostError] = useState(false)
    const [streamPostPath, setStreamPostPath] = useState<string | null>(null)
    // The hidden public post behind the share: tracked /s/ links point at it,
    // and "post to public stream" promotes it into the feed.
    const [backingPost, setBackingPost] = useState<DailyEarningsBackingPost | null>(null)
    const [trackedShare, setTrackedShare] = useState<{
        url: string
        reward_enabled: boolean
    } | null>(null)
    const [linkPreparing, setLinkPreparing] = useState(false)
    const [rewardInfoOpen, setRewardInfoOpen] = useState(false)

    const canUseMobileNativeShare = hasNativeCapability('nativeShare')
    const canUseNativeShare =
        canUseMobileNativeShare ||
        (typeof navigator !== 'undefined' && typeof navigator.share === 'function')
    const isMobileNativeFlow = canUseMobileNativeShare || (isMobile && canUseNativeShare)

    // Best link first: the tracked reward link, then the backing thread's /p/
    // short link, then the profile-resolving /p/<share id> fallback.
    const shareUrl =
        trackedShare?.url ||
        (backingPost?.answer_id ? getEarningsShareUrl(backingPost.answer_id) : null) ||
        (share ? getEarningsShareUrl(share.id) : null)
    const shareCardUrl = share ? getEarningsShareCardUrl(share.id) : null
    const shareText = buildDailyEarningsShareText(share?.amount_display)
    const dailyShareReady =
        !!shareUrl &&
        !!shareCardBlob &&
        !shareCardPreviewLoading &&
        !shareCardError &&
        !linkPreparing
    // The public post is the card marker, not the rendered image: posting only
    // needs the backing post to exist, so a slow or failed card render never
    // blocks it.
    const streamPostReady = !!share && !!backingPost && !linkPreparing

    // Mint (or fetch) today's share record when the tray opens.
    useEffect(() => {
        setShare(null)
        setMintError(null)
        if (!isOpen) return undefined

        let cancelled = false
        api.createDailyEarningsShare(day)
            .then(minted => {
                if (cancelled) return
                setShare(minted)
                queryClient.invalidateQueries({ queryKey: ['daily-earnings-today'] })
            })
            .catch(error => {
                if (cancelled) return
                console.error('Failed to mint daily earnings share', error)
                setMintError(error?.message || 'Could not create the share link.')
            })
        return () => {
            cancelled = true
        }
    }, [isOpen, day])

    // Once the share exists, ensure its backing post and mint the tracked
    // reward link against it — eagerly, so destination taps stay synchronous
    // (popup blockers punish links resolved after the click).
    useEffect(() => {
        setBackingPost(null)
        setTrackedShare(null)
        setLinkPreparing(false)
        setRewardInfoOpen(false)
        if (!isOpen || !share?.id) return undefined

        let cancelled = false
        setLinkPreparing(true)
        api.ensureDailyEarningsBackingPost(share.id)
            .then(async post => {
                if (cancelled || !post) return
                setBackingPost(post)
                setStreamPosted(!!post.stream_posted)
                setStreamPostPath(post.quest_id ? questPath(post.quest_id) : null)
                if (!post.answer_id) return
                try {
                    const link = await api.getAnonymousShareLink(post.answer_id)
                    if (!cancelled && link?.url) {
                        setTrackedShare({
                            url: link.url,
                            reward_enabled: link.reward_enabled === true,
                        })
                    }
                } catch {
                    // Reward link is a bonus; the thread link still works.
                }
            })
            .catch(error => {
                console.warn('Backing post unavailable, sharing the profile link', error)
            })
            .finally(() => {
                if (!cancelled) setLinkPreparing(false)
            })
        return () => {
            cancelled = true
        }
    }, [isOpen, share?.id])

    useEffect(() => {
        setShareCardSharing(false)
        setShareCardCopied(false)
        setShareCardError(null)
        setShareCardActionError(null)
        setShareCardBlob(null)
        setShareCardPreviewUrl(null)
        setShareCardPreviewLoading(false)
        setStreamPosting(false)
        setStreamPosted(false)
        setStreamPostError(false)
        setStreamPostPath(null)

        if (!isOpen || !shareCardUrl) return undefined

        setShareCardPreviewLoading(true)
        const requestController = new AbortController()
        let objectUrl: string | null = null
        let cancelled = false

        fetch(shareCardUrl, {
            credentials: 'same-origin',
            headers: { Accept: 'image/jpeg' },
            signal: requestController.signal,
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error(`Share card render failed (${response.status})`)
                }
                if (response.redirected) {
                    throw new Error('Share card renderer returned its fallback')
                }
                return response.blob()
            })
            .then(blob => {
                if (cancelled) return
                if (!blob.type.startsWith('image/')) {
                    throw new Error('Share card response was not an image')
                }

                objectUrl = URL.createObjectURL(blob)
                setShareCardBlob(blob)
                setShareCardPreviewUrl(objectUrl)
            })
            .catch(error => {
                if (cancelled || error?.name === 'AbortError') return
                console.error('Failed to load earnings card preview', error)
                setShareCardPreviewLoading(false)
                setShareCardError('Could not load that card. Please try again.')
            })

        return () => {
            cancelled = true
            requestController.abort()
            if (objectUrl) URL.revokeObjectURL(objectUrl)
        }
    }, [isOpen, shareCardUrl])

    if (!isOpen) return null
    if (typeof document === 'undefined') return null

    const shareSurface = resolveAnswerShareSurface({
        isMobileNativeFlow,
        canUseNativeShare: canUseNativeShare && dailyShareReady,
        canShareImageCard: !mintError,
        shareCardPreviewLoading: !share || shareCardPreviewLoading,
        shareCardReady: !!shareCardBlob,
        shareCardFailed: !!shareCardError,
        shareCardSharing,
    })

    const handleShareOptionClick = (ev: React.SyntheticEvent) => {
        ev.stopPropagation()
        window.setTimeout(() => {
            onDismiss?.()
        }, 0)
    }

    const getShareDestination = (destination: string) =>
        buildShareDestination({
            destination,
            shareUrl,
            shareText,
            shareTitle: EARNINGS_SHARE_TITLE,
        })

    const openShareDestination = (ev: React.SyntheticEvent, destination: string) => {
        if (!dailyShareReady) return

        handleShareOptionClick(ev)
        window.open(getShareDestination(destination).url, '_blank', 'noopener,noreferrer')
    }

    // Promotes the share's backing post into the public stream (server-side:
    // unhide + bump to the top of the feed). Idempotent per day, so tray
    // reopens can't double-post. Deliberately does NOT dismiss the tray: the
    // row flips to a green check and the user can keep sharing elsewhere.
    const handlePostToStream = async (ev: React.SyntheticEvent) => {
        ev.preventDefault()
        ev.stopPropagation()
        if (!streamPostReady || streamPosting || streamPosted) return

        setStreamPosting(true)
        setStreamPostError(false)
        try {
            const post = await api.promoteDailyEarningsPost(share.id)
            if (!post?.stream_posted) throw new Error('Public stream post returned no thread')
            setBackingPost(post)
            setStreamPostPath(post.quest_id ? questPath(post.quest_id) : null)
            setStreamPosted(true)
        } catch (error) {
            console.error('Failed to post daily earnings to the public stream', error)
            setStreamPostError(true)
        } finally {
            setStreamPosting(false)
        }
    }

    // "View" opens the promoted thread in-app. The anchor keeps its real
    // /quest/<id> href (new tab and modifier clicks still work), but a plain
    // click must not reload the whole app on that URL: it dismisses the tray
    // and its host, then opens the thread in the center panel like every
    // other thread link.
    const handleViewPost = (ev: React.MouseEvent<HTMLAnchorElement>) => {
        if (ev.button !== 0 || ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey) {
            ev.stopPropagation()
            return
        }
        ev.preventDefault()
        ev.stopPropagation()
        const questId = backingPost?.quest_id
        if (!questId) return

        onDismiss?.()
        onOpenPost?.()
        openCenter({ questId })
    }

    const shareWithNativeSheet = async () => {
        const payload = getShareDestination('native').payload

        if (canUseMobileNativeShare) {
            await shareTextOrUrlNative(payload)
        } else {
            await navigator.share(payload)
        }
    }

    const handleNativeShare = async (ev: React.SyntheticEvent) => {
        ev.preventDefault()
        ev.stopPropagation()

        if (!canUseNativeShare || !dailyShareReady) return

        try {
            await shareWithNativeSheet()
            onDismiss?.()
        } catch (error) {
            if ((error as Error)?.name === 'AbortError') return
        }
    }

    const handleCopyShareLink = (ev: React.SyntheticEvent) => {
        ev.preventDefault()
        ev.stopPropagation()
        if (!dailyShareReady) return

        insertToClipboard(shareUrl)
        setShareLinkCopied(true)
        window.setTimeout(() => {
            setShareLinkCopied(false)
        }, 1500)
    }

    const handleEmailShare = (ev: React.SyntheticEvent) => {
        ev.preventDefault()
        ev.stopPropagation()
        if (!dailyShareReady) return

        onDismiss?.()
        window.location.href = getShareDestination('email').url
    }

    const handleShareCard = async (ev: React.SyntheticEvent) => {
        ev.preventDefault()
        ev.stopPropagation()

        if (!share || !dailyShareReady || shareCardSharing) return

        setShareCardSharing(true)
        setShareCardActionError(null)

        try {
            if (isMobileNativeFlow) {
                await shareWithNativeSheet()
                onDismiss?.()
                return
            }

            if (!shareCardBlob || shareCardPreviewLoading) {
                throw new Error('Share card preview is still loading')
            }

            if (await copyShareCardToClipboard(shareCardBlob)) {
                setShareCardCopied(true)
                window.setTimeout(() => setShareCardCopied(false), 1500)
                return
            }

            const downloadUrl = URL.createObjectURL(shareCardBlob)
            const downloadLink = document.createElement('a')
            downloadLink.href = downloadUrl
            downloadLink.download = `treechat-earnings-${share.day}.jpg`
            document.body.appendChild(downloadLink)
            downloadLink.click()
            downloadLink.remove()
            window.setTimeout(() => URL.revokeObjectURL(downloadUrl), 10000)
            onDismiss?.()
        } catch (error) {
            if ((error as Error)?.name === 'AbortError') return

            console.error('Failed to share earnings card', error)
            setShareCardActionError('Could not share your earnings. Please try again.')
        } finally {
            setShareCardSharing(false)
        }
    }

    return createPortal(
        <Tray
            title="Share today's earnings"
            onDismiss={onDismiss}
            trayClassName={cn(trayStyles.shareTray, trayStyles.shareTrayDialog)}
            bodyClassName={trayStyles.shareTrayBody}
            closeLabel="Close share options"
            preventEscClose={rewardInfoOpen}
            preventBackdropClose={rewardInfoOpen}
            dialogTestId="earnings-share-tray"
            closeButtonTestId="earnings-share-tray-close"
        >
            <div className={trayStyles.shareOptions}>
                {mintError ? (
                    <div className={trayStyles.shareCardError} role="status">
                        {mintError}
                    </div>
                ) : (
                    <>
                        {trackedShare?.reward_enabled && (
                            <button
                                type="button"
                                className={trayStyles.shareRewardPill}
                                data-testid="earnings-share-reward-pill"
                                onClick={() => setRewardInfoOpen(true)}
                            >
                                <span className={trayStyles.shareRewardPillIcon}>
                                    <Coins size={16} aria-hidden="true" />
                                </span>
                                <span className={trayStyles.shareRewardPillText}>
                                    <strong>Earn 1¢</strong> for every new visitor you bring to
                                    Treechat
                                </span>
                                <Info
                                    size={15}
                                    className={trayStyles.shareRewardPillInfo}
                                    aria-hidden="true"
                                />
                            </button>
                        )}
                        <ShareRewardsInfoTray
                            isOpen={rewardInfoOpen}
                            onDismiss={() => setRewardInfoOpen(false)}
                        />
                        <div className={trayStyles.shareCardSection}>
                            {shareSurface.showDesktopDestinations && (
                                <div className={trayStyles.shareCardHeading}>share an image</div>
                            )}
                            {shareSurface.shareCardHeroVisible && (
                                <button
                                    type="button"
                                    className={trayStyles.shareCardHero}
                                    disabled={
                                        shareSurface.shareCardHeroDisabled || !dailyShareReady
                                    }
                                    aria-busy={
                                        shareCardSharing || (!dailyShareReady && !shareCardError)
                                    }
                                    aria-label={
                                        isMobileNativeFlow
                                            ? "Share today's earnings"
                                            : 'Copy the earnings card image'
                                    }
                                    onClick={handleShareCard}
                                >
                                    {shareCardPreviewUrl && (
                                        <img
                                            className={cn(
                                                trayStyles.shareCardHeroImage,
                                                !shareCardPreviewLoading &&
                                                    trayStyles.shareCardHeroImageReady,
                                            )}
                                            src={shareCardPreviewUrl}
                                            alt="Daily earnings share card preview"
                                            onLoad={() => setShareCardPreviewLoading(false)}
                                            onError={() => {
                                                setShareCardPreviewLoading(false)
                                                setShareCardError(
                                                    'Could not load that card. Please try again.',
                                                )
                                            }}
                                        />
                                    )}
                                    {!dailyShareReady && !shareCardError && (
                                        <span
                                            className={trayStyles.shareCardHeroShimmer}
                                            aria-hidden="true"
                                        />
                                    )}
                                    <span className={trayStyles.shareCardHeroPill}>
                                        {shareCardSharing ? (
                                            isMobileNativeFlow ? (
                                                'sharing…'
                                            ) : (
                                                'copying…'
                                            )
                                        ) : !shareCardBlob || shareCardPreviewLoading ? (
                                            'loading preview…'
                                        ) : !dailyShareReady ? (
                                            'preparing link…'
                                        ) : isMobileNativeFlow ? (
                                            <>
                                                <Share2 size={13} aria-hidden="true" />
                                                share
                                            </>
                                        ) : shareCardCopied ? (
                                            'copied!'
                                        ) : (
                                            <>
                                                <Copy size={13} aria-hidden="true" />
                                                copy image
                                            </>
                                        )}
                                    </span>
                                </button>
                            )}
                            {(shareCardError || shareCardActionError) && (
                                <div className={trayStyles.shareCardError} role="status">
                                    {shareCardError || shareCardActionError}
                                </div>
                            )}
                        </div>

                        {streamPosted && streamPostPath ? (
                            <div
                                className={cn(
                                    trayStyles.shareOptionButton,
                                    trayStyles.shareOptionPosted,
                                )}
                                data-testid="earnings-share-post-to-stream"
                            >
                                <div
                                    className={cn(
                                        trayStyles.shareOptionIcon,
                                        trayStyles.shareOptionIconRound,
                                        trayStyles.shareOptionIconSuccess,
                                    )}
                                    data-testid="earnings-share-stream-icon"
                                >
                                    <Check size={20} strokeWidth={3} />
                                </div>
                                <span className={trayStyles.shareOptionLabel}>
                                    posted to public stream
                                </span>
                                <a
                                    className={trayStyles.shareOptionViewLink}
                                    href={streamPostPath}
                                    onClick={handleViewPost}
                                    data-testid="earnings-share-view-post"
                                >
                                    View
                                    <ChevronRight size={14} strokeWidth={2.5} aria-hidden />
                                </a>
                            </div>
                        ) : (
                            <button
                                type="button"
                                className={trayStyles.shareOptionButton}
                                onClick={handlePostToStream}
                                disabled={!streamPostReady || streamPosting}
                                data-testid="earnings-share-post-to-stream"
                            >
                                <div
                                    className={cn(
                                        trayStyles.shareOptionIcon,
                                        trayStyles.shareOptionIconRound,
                                    )}
                                    data-testid="earnings-share-stream-icon"
                                >
                                    <TreechatIcon
                                        size={36}
                                        className={trayStyles.shareOptionBrandIcon}
                                    />
                                </div>
                                <span className={trayStyles.shareOptionLabel}>
                                    {streamPosting
                                        ? 'posting…'
                                        : streamPostError
                                        ? 'post failed — tap to retry'
                                        : 'post to public stream'}
                                </span>
                            </button>
                        )}

                        {shareSurface.showNativeFallback && (
                            <button
                                type="button"
                                className={trayStyles.shareOptionButton}
                                onClick={handleNativeShare}
                                disabled={!dailyShareReady}
                            >
                                <div
                                    className={cn(
                                        trayStyles.shareOptionIcon,
                                        trayStyles.shareOptionIconRound,
                                    )}
                                >
                                    <Share2 size={20} />
                                </div>
                                <span className={trayStyles.shareOptionLabel}>share</span>
                            </button>
                        )}

                        <button
                            type="button"
                            className={trayStyles.shareOptionButton}
                            onClick={handleCopyShareLink}
                            disabled={!dailyShareReady}
                        >
                            <div
                                className={cn(
                                    trayStyles.shareOptionIcon,
                                    trayStyles.shareOptionIconRound,
                                )}
                            >
                                <Link size={20} />
                            </div>
                            <span className={trayStyles.shareOptionLabel}>
                                {shareLinkCopied ? 'link copied!' : 'copy link'}
                            </span>
                        </button>

                        {shareSurface.showDesktopDestinations && (
                            <button
                                type="button"
                                className={trayStyles.shareOptionButton}
                                onClick={ev => openShareDestination(ev, 'x')}
                                disabled={!dailyShareReady}
                            >
                                <TwitterIcon
                                    size={36}
                                    round
                                    className={trayStyles.shareOptionIcon}
                                />
                                <span className={trayStyles.shareOptionLabel}>share to x</span>
                            </button>
                        )}

                        {shareSurface.showDesktopDestinations && (
                            <button
                                type="button"
                                className={trayStyles.shareOptionButton}
                                onClick={ev => openShareDestination(ev, 'facebook')}
                                disabled={!dailyShareReady}
                            >
                                <FacebookIcon
                                    size={36}
                                    round
                                    className={trayStyles.shareOptionIcon}
                                />
                                <span className={trayStyles.shareOptionLabel}>
                                    share to facebook
                                </span>
                            </button>
                        )}

                        {shareSurface.showDesktopDestinations && (
                            <button
                                type="button"
                                className={trayStyles.shareOptionButton}
                                onClick={ev => openShareDestination(ev, 'reddit')}
                                disabled={!dailyShareReady}
                            >
                                <RedditIcon
                                    size={36}
                                    round
                                    className={trayStyles.shareOptionIcon}
                                />
                                <span className={trayStyles.shareOptionLabel}>share to reddit</span>
                            </button>
                        )}

                        {shareSurface.showDesktopDestinations && (
                            <button
                                type="button"
                                className={trayStyles.shareOptionButton}
                                onClick={ev => openShareDestination(ev, 'telegram')}
                                disabled={!dailyShareReady}
                            >
                                <TelegramIcon
                                    size={36}
                                    round
                                    className={trayStyles.shareOptionIcon}
                                />
                                <span className={trayStyles.shareOptionLabel}>
                                    share to telegram
                                </span>
                            </button>
                        )}

                        {shareSurface.showDesktopDestinations && (
                            <button
                                type="button"
                                className={trayStyles.shareOptionButton}
                                onClick={handleEmailShare}
                                disabled={!dailyShareReady}
                            >
                                <EmailIcon size={36} round className={trayStyles.shareOptionIcon} />
                                <span className={trayStyles.shareOptionLabel}>share via email</span>
                            </button>
                        )}

                        {shareSurface.showDesktopDestinations && canUseNativeShare && (
                            <button
                                type="button"
                                className={trayStyles.shareOptionButton}
                                onClick={handleNativeShare}
                                disabled={!dailyShareReady}
                            >
                                <div
                                    className={cn(
                                        trayStyles.shareOptionIcon,
                                        trayStyles.shareOptionIconRound,
                                    )}
                                >
                                    <Share2 size={20} />
                                </div>
                                <span className={trayStyles.shareOptionLabel}>
                                    share with device
                                </span>
                            </button>
                        )}
                    </>
                )}
            </div>
        </Tray>,
        document.body,
    )
}
